<template>
    <page-layout>
        <div :class="mainClass">
            <button class="menu-close" @click.stop.prevent="toggleBurger" v-if="burgerMenuOpen"></button>
            <aside :class="asideNavigationClass">
                <slot name="aside-before"/>
                <b-list-group>
                    <h4 v-if="isMobile">{{ $t('navigation.menuHeader') }}</h4>
                    <div v-for="(link) in activeLinks(links)" :key="link.name">
                        <b-list-group-item class="menu-item" @click="linker(link)" :key="link.name"
                                           :active="isActive(link)">
                            <component class="icon" :is="link.icon"/>
                            {{ link.label }}
                        </b-list-group-item>
                    </div>
                </b-list-group>

                <section v-if="isMobile">
                    <h4>{{ $t('navigation.storeHeader') }}</h4>
                    <b-list-group>
                        <div v-for="(item) in menu">
                            <b-list-group-item class="menu-item" :href="item.normalizedhref" :key="item.name">
                                {{ item.name }}
                            </b-list-group-item>
                        </div>
                    </b-list-group>
                </section>
                <slot name="aside-after"/>
            </aside>
            <main :class="mainContentClass">
                <important-alert show v-if="!verificationSent && needsEmailValidation && !missingEmail"
                                 variant="danger">
                    {{ $t('account.alert.emailVerificationNeeded', { email: email }) }}
                    <b-button v-if="!sending" size="sm" @click="sendEmailVerification">
                        {{ $t('account.sendVerifyEmailAgainButton') }}
                    </b-button>
                    <b-spinner small v-if="sending"></b-spinner>
                </important-alert>
                <b-alert show v-if="verificationSent && needsEmailValidation" variant="success">
                    {{ $t('account.sendVerificationEmailSuccess') }}
                    <b-button v-if="!sending" size="sm" @click="sendEmailVerification">
                        {{ $t('account.sendVerifyEmailAgainButton') }}
                    </b-button>
                    <b-spinner small v-if="sending"></b-spinner>
                </b-alert>
                <important-alert show v-if="missingEmail">
                    {{ $t('account.alert.addMissingEmail') }}
                    <b-button :to="{ name: 'account.details.setEmail' } ">
                        {{ $t('account.alert.addMissingEmailGoToDetailsButton') }}
                    </b-button>
                </important-alert>
                <slot/>
            </main>
        </div>
    </page-layout>
</template>
<script>
import { defineComponent, unref } from 'vue';
import PageLayout from '@components/Layout/PageLayout.vue';
import { useUserStore } from '@stores/User';
import { useAppStore } from '@stores/App';
import HomeIcon from '@components/Icons/HomeIcon.vue';
import OrderHistoryIcon from '@components/Icons/OrderHistoryIcon.vue';
import LogoutIcon from '@components/Icons/LogoutIcon.vue';
import PromotionsIcon from '@components/Icons/PromotionsIcon.vue';
import WishlistIcon from '@components/Icons/WishlistIcon.vue';
import { api } from '@services/AxiosBootstrap';
import AccountIcon from '@components/Icons/AccountIcon.vue';
import ImportantAlert from '@components/Alerts/ImportantAlert.vue';
import WeddingGiftIcon from '@components/Icons/WeddingGiftIcon.vue';

export default defineComponent({
    components: { ImportantAlert, PageLayout },
    data() {
        const $t = this.$t;
        return {
            sending: false,
            verificationSent: false,
            menu: [],
            links: [
                {
                    label: $t('account.navigation.home'),
                    name: 'account.home',
                    icon: HomeIcon,
                },
                {
                    label: $t('account.navigation.promotions'),
                    name: 'account.promotions',
                    icon: PromotionsIcon,
                },
                {
                    label: $t('account.navigation.details'),
                    name: 'account.details',
                    icon: AccountIcon,
                },
                {
                    label: $t('account.navigation.history'),
                    name: 'account.history',
                    icon: OrderHistoryIcon,
                },
                {
                    label: $t('account.navigation.lists'),
                    name: 'account.lists',
                    icon: WishlistIcon,
                },
                {
                    label: $t('account.navigation.weddingList'),
                    name: 'account.weddingList',
                    icon: WeddingGiftIcon,
                },
                {
                    label: $t('account.navigation.logout'),
                    name: 'account.logout',
                    icon: LogoutIcon,
                },
            ]
        };
    },
    async mounted() {
        const app = useAppStore();
        this.menu = app.getMainNavigation();
    },
    computed: {
        needsEmailValidation() {
            const user = useUserStore();
            return user.needsMailVerification;
        },
        missingEmail() {
            const user = useUserStore();
            return unref(user?.needsEmail);
        },
        asideNavigationClass() {
            const app = useAppStore();
            const open = app.burgerMenuOpen ? 'np-open' : 'np-close';
            return [ unref(app.getBreakpoints().active()), 'main-page__navigation', open ];
        },
        mainContentClass() {
            const app = useAppStore();
            return [ unref(app.getBreakpoints().active()), 'main-page__content' ];
        },
        mainClass() {
            const app = useAppStore();
            return [ unref(app.getBreakpoints().active()), 'main-page' ];
        },
        isMobile() {
            const app = useAppStore();
            const active = app.getBreakpoints().active();
            return [ 'sm', 'md' ].includes(active.value);
        },
        burgerMenuOpen() {
            const app = useAppStore();
            return app.burgerMenuOpen && this.isMobile;
        },
        email() {
            const user = useUserStore();
            return user.email;
        },
    },
    methods: {
        toggleBurger() {
            const app = useAppStore();
            return app.burgerMenuOpen = !app.burgerMenuOpen;
        },
        isActive(link) {
            return this.$route.name === link;
        },
        activeLinks(links) {
            const app = useAppStore();

            if (app.isDevelopment()) {
                return links;
            }

            return links;
        },
        linker(link) {
            if (this.isMobile) {
                this.toggleBurger();
            }
            this.$router.push({ name: link.name });
        },
        async sendEmailVerification() {
            this.sending = true;
            this.verificationSent = false;
            try {
                await api.post('/auth/send-verification-email');
                this.verificationSent = true;
            } catch (e) {
                this.$bvToast.toast(this.$t('account.sendVerificationEmailFailed'), {
                    variant: 'danger',
                    title: this.$t('account.sendVerificationEmailError'),
                });
            }
            this.sending = false;
        }
    },
});
</script>
<style lang="scss" scoped>
.main-page {
    display: grid;
    gap: 2rem;
    grid-template-columns: 25% 1fr;
}

.icon {
    width: 1em !important;
    margin-right: 0.5em !important;
}

.menu-item {
    cursor: pointer;
    border: 0;
    border-bottom: #E0E0E0 solid 1px;
    font-weight: 300;
    font-family: Optima, serif;
    font-size: 14px;
}

.menu-close {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: none;
}

.sm,
.md {
    &.main-page {
        padding: 1rem;
        grid-template-columns: 1fr;

        &__content {
            max-width: 100%;
            overflow: hidden;
        }
    }

    &.main-page__navigation {
        position: fixed;
        top: 0;
        left: 0;
        right: 10%;
        bottom: 0;
        transform: translateX(-100%);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        background: white;
        z-index: 100;
        max-width: 300px;
        max-height: 100dvh;
        overflow-y: auto;

        h4 {
            font-family: Chronicle, serif;
            padding: 1rem;
            margin-bottom: 0;
        }

        &.np-open {
            transform: translateX(0%);
            box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
        }
    }
}
</style>
